import { useMemo } from 'react';

import { IAccountDtoAccountTypeEnum } from '@/dtos';
import { AccountOrder, Enrollment, PolicyEligibleService } from '@/modules/UseMyMoney/queries';

import { CombinedAccountData } from '../AccountDetails.types';

interface Props {
  enrollments?: Enrollment[];
  accounts?: AccountOrder[];
  policyEligibleServices?: PolicyEligibleService[];
  isLoading?: boolean;
}

const useCombinedAccountData = ({ enrollments, accounts, policyEligibleServices, isLoading }: Props) => {
  const combinedAccountsData = useMemo(() => {
    if (isLoading) {
      return [];
    }

    const enrollmentsWithAccountData = enrollments?.map((enrollment): CombinedAccountData | null => {
      const accountOrder = accounts?.find((accountOrder) => enrollment?.accountId === accountOrder?.accountId);
      return {
        enrollment,
        accountOrder,
      };
    })
      .filter((item): item is CombinedAccountData => item !== null)
      .sort((a, b) => {
        const aOrder = a?.accountOrder?.order ?? Number.POSITIVE_INFINITY;
        const bOrder = b?.accountOrder?.order ?? Number.POSITIVE_INFINITY;

        return aOrder - bOrder;
      });

    const enrollmentsWithCategories = enrollmentsWithAccountData?.map((enrollmentWithAccountData) => {
      const categories = policyEligibleServices?.find((policyEligibleService) => policyEligibleService?.policyId === enrollmentWithAccountData?.enrollment?.policyId);
      return {
        ...enrollmentWithAccountData,
        policyEligibleService: categories,
      };
    });

    const sortedAccounts = enrollmentsWithCategories?.sort((prev, next) => {
      const isHSAPrev = prev?.enrollment?.accountType === IAccountDtoAccountTypeEnum.HSA;
      const isHSANext = next?.enrollment?.accountType === IAccountDtoAccountTypeEnum.HSA;
      
      if (isHSAPrev && !isHSANext) return 1;
      if (!isHSAPrev && isHSANext) return -1;
      return 0;
    }) || [];

    return sortedAccounts;
  }, [enrollments, accounts, policyEligibleServices, isLoading]);

  return {
    combinedAccountsData
  };
};

export default useCombinedAccountData;
